import { HttpResponse } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, HostListener, OnInit } from '@angular/core';
import { GetCurrentAccountDocumentDetails } from '@app/finxone-web-frontend/app/lib/actions/account.action';
import { GetSavingAccountDocumentDetails } from '@app/finxone-web-frontend/app/lib/actions/savings-account.action';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import { DocumentService } from '@app/finxone-web-frontend/app/lib/services/document-service/document.service';
import { AccountState } from '@app/finxone-web-frontend/app/lib/state/account.state';
import {
  ApiActions,
  ApiLoadingState,
  UpdateApiIsLoadingAction,
} from '@app/finxone-web-frontend/app/lib/state/api-loading.state';
import { SavingsAccountState } from '@app/finxone-web-frontend/app/lib/state/savings-account.state';
import { TransactionState } from '@app/finxone-web-frontend/app/lib/state/transaction.state';
import { downloadFile } from '@app/finxone-web-frontend/app/lib/utils/cta-button-actions/document-actions';
import { scrollLoading } from '@app/finxone-web-frontend/app/lib/utils/scrolling-utils/scrolling.utils';
import {
  buildHandleBarModel,
  filterIfHandlebarNotRendered,
} from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { IconCacheService } from '@finxone-platform/shared/services';
import {
  AppBuilderPreviewState,
  BaseCssConfig,
  baseCssConfigStyle,
  BaseCssProperties,
  DocumentActionType,
  DownloadDocumentRequestType,
  FileDocumentResponse,
  Orientation,
  ProviderClass,
  resolveSkeletonLoadingTemplate,
  resolveSkeletonRenderOptions,
  SKELETON_LAYOUT_CONFIG_PATH,
  WidgetNames,
} from '@finxone-platform/shared/sys-config-types';
import { Select, Store } from '@ngxs/store';
import Handlebars from 'handlebars';
import { debounceTime, Observable, Subject, takeUntil } from 'rxjs';
import { take } from 'rxjs/operators';
import { PageConfigurationProvider } from '../../../zones/page-configuration.provider';
import {
  GetTransactionReceiptFile,
  GetTransactionReceiptFileId,
} from './../../../../lib/actions/transactions.action';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-document-listing-widget',
  templateUrl: './document-listing-widget.component.html',
})
export class DocumentListWidgetComponent implements OnInit, AfterViewInit {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  private flutterWebView = (window as any).flutter_inappwebview;
  public noDocumentTemplateFunction: HandlebarsTemplateDelegate<any>;
  public documentListTemplateFunction: HandlebarsTemplateDelegate<any>;
  public fetchingTemplateFunction: HandlebarsTemplateDelegate<any>;
  @Select(ApiLoadingState.getApiState(ApiActions.documentsList))
  private getDocumentsApiState$: Observable<{ isLoading: boolean }>;
  @Select(ApiLoadingState.getApiState(ApiActions.documentDownload))
  private documentDownloadApiState$: Observable<{ isLoading: boolean }>;
  public documentContext: DocumentActionType;
  public isApiInProgress = false;
  public widgetName = WidgetNames.DOCUMENT_LISTING_WIDGET;
  public fetchingTemplate = '';
  public noDocumentTemplate = '';
  public documentListTemplate: string;
  public documentDetails: FileDocumentResponse[] = [];
  public containerHeight: string;
  public Orientation = Orientation;
  public orientation: Orientation;
  public carouselItemStyles: baseCssConfigStyle & { widthUnit: string } = {
    width: '100%',
    height: '100%',
    widthUnit: '%',
  };
  public pageNumber = 1;
  public leftIconStyle: baseCssConfigStyle;
  public rightIconStyle: baseCssConfigStyle;
  public leftIcon: string;
  public rightIcon: string;
  public contentStyle: baseCssConfigStyle;
  public baseCssConfig: BaseCssConfig;
  public appBuilderShowFetchingContent = false;
  public appBuilderShowNoRecordsContent = false;
  public selectedTextStyleClass: string;
  public isDownloading = false;
  public documentId: string | null = null;

  constructor(
    private store: Store,
    public dataIn: ProviderClass<any>,
    private countriesCurrenciesService: CountriesCurrenciesService,
    public pageConfig: PageConfigurationProvider,
    private iconCacheService: IconCacheService,
    private el: ElementRef,
    private configService: ConfigService,
    private documentService: DocumentService,
  ) {}

  ngOnInit() {
    this.loadWidgetConfig();
    this.loadBaseStyles();
    this.getDocumentBasedOnContext();
    this.onGetCalledLoaderSubscription();
  }

  ngAfterViewInit() {
    this.containerHeight = scrollLoading('document-list', this.el);
  }

  private loadBaseStyles() {
    this.baseCssConfig = this.dataIn.attri?.css_config;
    this.contentStyle = this.configService.getSpecificBaseCssStyle(this.dataIn?.attri, [
      BaseCssProperties.MARGIN,
      BaseCssProperties.PADDING,
      BaseCssProperties.COLORS_V2,
      BaseCssProperties.BORDER_V2,
      BaseCssProperties.BOX_SHADOW,
      BaseCssProperties.TEXTALIGN,
      BaseCssProperties.V_ALIGN,
    ]);

    this.leftIconStyle = this.configService.getSpecificBaseCssStyle(this.dataIn?.attri, [
      BaseCssProperties.LEFT_ICON_SIZE,
      BaseCssProperties.LEFT_ICON_COLOR,
    ]);
    this.rightIconStyle = this.configService.getSpecificBaseCssStyle(this.dataIn?.attri, [
      BaseCssProperties.RIGHT_ICON_SIZE,
      BaseCssProperties.RIGHT_ICON_COLOR,
    ]);
  }

  private loadWidgetConfig() {
    this.orientation = this.dataIn?.attri?.widgetProperties?.['orientation'] ?? Orientation.VERTICAL;
    this.selectedTextStyleClass = this.dataIn.attri?.widgetProperties?.textStyleClass ?? '';
    this.carouselItemStyles = {
      height:
        this.dataIn?.attri?.widgetProperties?.carouselItemHeight +
          this.dataIn?.attri?.widgetProperties?.carouselItemHeightUnit || 'auto',
      width: this.dataIn?.attri?.widgetProperties?.carouselItemWidth ?? 'auto',
      widthUnit: this.dataIn?.attri?.widgetProperties?.['carouselItemWidthUnit'] ?? '%',
    };
    const itemTemplate =
      this.orientation === Orientation.HORIZONTAL
        ? this.dataIn.attri?.widgetProperties?.horizontalLayoutTemplate
        : this.dataIn.attri?.widgetProperties?.documentListItemTemplate;

    this.noDocumentTemplateFunction = Handlebars.compile(
      this.dataIn?.attri?.widgetProperties?.['noDocumentTemplate'] ?? 'No Records',
    );
    this.fetchingTemplateFunction = Handlebars.compile(
      this.dataIn?.attri?.widgetProperties?.['fetchingTemplate'] ?? 'Fetching',
    );
    this.documentListTemplateFunction = Handlebars.compile(itemTemplate);
    this.iconStyles();
    buildHandleBarModel(
      this.store,
      '',
      this.countriesCurrenciesService,
      this.iconCacheService,
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe((model) => {
        this.noDocumentTemplate = filterIfHandlebarNotRendered(this.noDocumentTemplateFunction(model));
        this.fetchingTemplate = this.resolveFetchingTemplate(
          filterIfHandlebarNotRendered(this.fetchingTemplateFunction(model)),
        );
      });

    this.setAppBuilderPreviewState();
  }

  /**
   * The designed skeleton wins over the widget's own fetching template, so the live app and the app
   * builder canvas render the same loading state from one config. The render options carry the
   * widget's orientation and carousel item width, so a horizontal list loads as a row of cards
   * rather than stacked rows.
   */
  private resolveFetchingTemplate(renderedFetchingTemplate: string): string {
    return resolveSkeletonLoadingTemplate(
      this.dataIn?.attri?.[SKELETON_LAYOUT_CONFIG_PATH],
      renderedFetchingTemplate,
      undefined,
      resolveSkeletonRenderOptions(this.dataIn?.attri),
    );
  }

  /**
   * Preview states belong to the app builder canvas only, so a project published while one was
   * selected still loads its real loading and empty states for a customer. Both flags are assigned
   * rather than only set, so switching the preview back to Default clears them.
   */
  private setAppBuilderPreviewState(): void {
    if (!this.configService.getActiveConfigType().isWorkflow) {
      return;
    }
    const appBuilderState = this.dataIn?.attri?.widgetProperties?.['appBuilderState'];
    this.appBuilderShowFetchingContent = appBuilderState === AppBuilderPreviewState.FETCHING;
    this.appBuilderShowNoRecordsContent = appBuilderState === AppBuilderPreviewState.NO_RECORDS;
  }

  private onGetCalledLoaderSubscription(): void {
    this.getDocumentsApiState$.pipe(takeUntil(this.destroy$)).subscribe((result) => {
      this.isApiInProgress = result.isLoading;
    });

    this.documentDownloadApiState$.pipe(takeUntil(this.destroy$)).subscribe((result) => {
      this.isDownloading = result.isLoading;
    });
  }

  private iconStyles() {
    this.leftIcon = this.dataIn.attri?.widgetProperties?.lefticon ?? '';
    this.rightIcon = this.dataIn.attri?.widgetProperties?.righticon ?? '';
  }

  public getDocumentBasedOnContext() {
    this.documentContext = this.dataIn?.attri?.widgetProperties?.['documentContext'];
    switch (this.documentContext) {
      case DocumentActionType.CURRENT_ACCOUNTS:
        this.getCurrentAccountDocumentList();
        break;
      case DocumentActionType.SAVING_ACCOUNTS:
        this.getSavingAccountDocumentList();
        break;
      case DocumentActionType.TRANSACTIONS:
        this.getTransactionDocumentList();
        break;

      default:
        break;
    }
  }

  private getCurrentAccountDocumentList() {
    const currentAccountId = this.store.selectSnapshot(AccountState.getCurrentAccountId);
    this.store
      .dispatch(
        new GetCurrentAccountDocumentDetails(
          this.dataIn?.attri?.widgetProperties?.['documentContext'],
          this.dataIn?.attri?.widgetProperties?.['documentSubCategory'],
          currentAccountId,
        ),
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const state = this.store.selectSnapshot(AccountState.getAccounts);
        if (state?.documentDetails?.length) {
          this.documentDetails = state.documentDetails ?? [];
        }
      });
  }

  private getSavingAccountDocumentList() {
    const currentAccountId = this.store.selectSnapshot(SavingsAccountState.getCurrentAccountId);
    this.store
      .dispatch(
        new GetSavingAccountDocumentDetails(
          this.dataIn?.attri?.widgetProperties?.['documentContext'],
          this.dataIn?.attri?.widgetProperties?.['documentSubCategory'],
          currentAccountId,
        ),
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const state = this.store.selectSnapshot(SavingsAccountState.getAccounts);
        if (state?.documentDetails?.length) {
          this.documentDetails = state.documentDetails ?? [];
        }
      });
  }

  private getTransactionDocumentList() {
    const currentTransactionData = this.store.selectSnapshot(TransactionState.getTransactions);
    this.store
      .dispatch(
        new GetTransactionReceiptFileId(
          this.dataIn?.attri?.widgetProperties?.['documentContext'],
          currentTransactionData.currentTransactionId,
          this.dataIn?.attri?.widgetProperties?.['documentSubCategory'],
        ),
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const state = this.store.selectSnapshot(TransactionState.getTransactions);
        if (state?.transactionReceiptDetails?.length) {
          this.documentDetails = state.transactionReceiptDetails;
        }
      });
  }

  public renderDocumentItem(documentData: FileDocumentResponse, index: number): string {
    return this.documentListTemplateFunction({
      document: {
        ...documentData,
      },
      index,
    });
  }

  @HostListener('scroll', ['$event'])
  onScroll(event: Event) {
    const element = event.target as HTMLElement;
    const scrollPercentage = (element.scrollTop / (element.scrollHeight - element.clientHeight)) * 100;
    if (scrollPercentage > 80 && !this.store.selectSnapshot(TransactionState.getLoadingStatus)) {
      // TODO currently pagination will not support
      if (this.dataIn?.attri?.widgetProperties?.['pagingEnabled']) {
        this.pageNumber = this.pageNumber + 1;
      }
    }
  }

  public onClickDocument(document: FileDocumentResponse) {
    this.documentId = document.id;
    const payload = {
      category: document?.category,
      fileId: document?.fileId,
    } as DownloadDocumentRequestType;
    const extensionType = document?.path?.split('.')?.pop()?.toLowerCase() || 'pdf';

    this.documentContext = this.dataIn?.attri?.widgetProperties?.['documentContext'];
    const currentTransactionData = this.store.selectSnapshot(TransactionState.getTransactions);
    const transactionId = currentTransactionData?.currentTransactionId;

    switch (this.documentContext) {
      case DocumentActionType.TRANSACTIONS:
        if (!transactionId) {
          console.error('Transaction ID not found.');
          return;
        }
        this.store.dispatch(new UpdateApiIsLoadingAction(ApiActions.documentDownload, true));
        this.store
          .dispatch(
            new GetTransactionReceiptFile(
              DocumentActionType.TRANSACTIONS,
              currentTransactionData.currentTransactionId,
              'download',
            ),
          )
          .pipe(take(1), debounceTime(100))
          .subscribe({
            next: () => {
              const transactionState = this.store.selectSnapshot(TransactionState.getTransactions);
              const url = transactionState?.transactionReceiptBlobUrl;
              if (this.flutterWebView) {
                this.flutterWebView.callHandler('fileDownloadHandler', [url]);
              } else if (url) {
                downloadFile(url, this.documentContext);
              }
              this.store.dispatch(new UpdateApiIsLoadingAction(ApiActions.documentDownload, false));
              this.documentId = null;
            },
            error: (err) => {
              this.store.dispatch(new UpdateApiIsLoadingAction(ApiActions.documentDownload, false));
              this.documentId = null;
              console.error('Error', err);
            },
          });
        break;

      default:
        if (this.flutterWebView) {
          // Get base URL from configService
          this.configService
            .getApi('account_service')
            .pipe(take(1))
            .subscribe((baseUrl) => {
              const downloadApiUrl = `${baseUrl}/documents/${this.documentContext}/${document.entityId}/download`;
              //Flutter Request data
              const flutterRequestData = {
                url: downloadApiUrl,
                payload: payload,
                method: 'post',
                extension: extensionType,
              };
              this.flutterWebView.callHandler('fileDownloadHandler', [flutterRequestData]);
            });
        } else {
          this.store.dispatch(new UpdateApiIsLoadingAction(ApiActions.documentDownload, true));
          this.documentService
            .downloadDocument(this.documentContext, document.entityId, payload)
            .pipe(take(1), debounceTime(100))
            .subscribe({
              next: (response) => {
                this.downloadResponse(response, extensionType);
                this.store.dispatch(new UpdateApiIsLoadingAction(ApiActions.documentDownload, false));
                this.documentId = null;
              },
              error: (err) => {
                this.store.dispatch(new UpdateApiIsLoadingAction(ApiActions.documentDownload, false));
                this.documentId = null;
                console.error('Error downloading document:', err);
              },
            });
        }
        break;
    }
  }

  private downloadResponse(response: HttpResponse<Blob>, documentType: string) {
    const blob = response.body;
    if (!blob) {
      return;
    }
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;

    const contentDisposition =
      response.headers.get('content-disposition') ?? response.headers.get('Content-Disposition');
    const extractedFileName = this.getFileNameFromContentDisposition(contentDisposition);
    a.download = extractedFileName || new Date().getTime() + `.${documentType}`;

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(blobUrl);
  }

  private getFileNameFromContentDisposition(contentDisposition: string | null): string | null {
    if (!contentDisposition) {
      return null;
    }

    const utf8Match = contentDisposition.match(/filename\*=(?:UTF-8''|utf-8'')([^;]+)/);
    if (utf8Match?.[1]) {
      const value = utf8Match[1].trim();
      try {
        return decodeURIComponent(value.replace(/(^")|("$)/g, ''));
      } catch {
        return value.replace(/(^")|("$)/g, '');
      }
    }

    const plainMatch = contentDisposition.match(/filename=([^;]+)/);
    if (plainMatch?.[1]) {
      return plainMatch[1].trim().replace(/(^")|("$)/g, '');
    }

    return null;
  }
}
